import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import Footer from "@/components/Footer";
import { ShareButtons } from "@/components/ShareButtons";
import { INSIGHTS } from "@/data/insights";

export default function Article() {
  const { slug } = useParams();
  const article = INSIGHTS.find((a) => a.slug === slug);

  useEffect(() => {
    if (!article) return;
    const prev = document.title;
    document.title = `${article.title} — AiCendor`;
    window.scrollTo(0, 0);
    return () => {
      document.title = prev;
    };
  }, [article]);

  if (!article) return <Navigate to="/#insights" replace />;

  const others = INSIGHTS.filter((a) => a.slug !== slug);

  return (
    <div className="relative min-h-screen bg-ink text-white">
      <div className="noise-overlay" aria-hidden="true" />
      <header className="border-b border-white/10">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
          <Link to="/" aria-label="AiCendor home" data-testid="article-logo-home"><Logo /></Link>
          <Link to="/#insights" data-testid="article-back" className="link-underline flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-300 hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" /> All insights
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-20 lg:px-10 lg:py-28" data-testid="article-page">
        <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-gold">
          <span>{article.category}</span>
          <span className="h-px w-6 bg-gold/50" aria-hidden="true" />
          <span className="text-slate-500">{article.date} · {article.readTime}</span>
        </p>
        <h1 className="mt-8 font-display text-3xl leading-tight tracking-tight sm:text-5xl" data-testid="article-title">{article.title}</h1>
        <p className="mt-8 text-base leading-relaxed text-slate-300 sm:text-lg">{article.excerpt}</p>
        <div className="mt-8 border-y border-white/10 py-5">
          <ShareButtons article={article} />
        </div>
        <div className="mt-10 space-y-6">
          {article.body.map((p, i) => (
            <p key={i} className="text-sm leading-[1.85] text-slate-300 sm:text-base">{p}</p>
          ))}
        </div>

        <div className="mt-16 glass-panel flex flex-col items-start gap-5 p-8 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">Talk it through</p>
            <p className="mt-2 text-sm text-slate-300">A 30-minute call with a senior AiCendor consultant — no obligation.</p>
          </div>
          <Link to="/#book" data-testid="article-book-cta" className="btn-sweep btn-sweep-light flex items-center gap-2 border border-accent bg-accent px-6 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-ink">
            Book a consultation <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="mt-20">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-slate-500">More insights</p>
          <ul className="mt-6 divide-y divide-white/10 border-y border-white/10">
            {others.map((a) => (
              <li key={a.slug}>
                <Link to={`/insights/${a.slug}`} data-testid={`article-more-${a.slug}`} className="group flex items-center justify-between gap-6 py-5">
                  <span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">{a.category}</span>
                    <span className="mt-1 block font-display text-lg leading-snug text-white transition-colors group-hover:text-gold">{a.title}</span>
                  </span>
                  <ArrowUpRight className="h-4 w-4 shrink-0 text-slate-500 transition-colors group-hover:text-gold" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </main>
      <Footer />
    </div>
  );
}
